import Vue from 'vue';
import VueRouter from 'vue-router';
import { TokenService } from '@/api/service';
import { getPaymentHistory } from '@/api/modules/payment';

Vue.use(VueRouter);

// 定义需要登录才能访问的路由 - 只有论坛和学术资源
const authRoutes = [
  '/forum',
  '/forum/topic',
  '/resources'
];

const routes = [
  {
    path: '/',
    redirect: '/home',
  },
  {
    path: '/home',
    name: 'Home',
    component: () => import(/* webpackChunkName: "home" */ '../views/Home.vue'),
  },
  // --- 新增的静态页面路由 ---
  {
    path: '/about/introduction',
    name: 'SocietyIntroduction',
    component: () => import(/* webpackChunkName: "about" */ '../views/SocietyIntroduction.vue'),
  },
  {
    path: '/about/constitution',
    name: 'SocietyCharter',
    component: () => import(/* webpackChunkName: "about" */ '../views/SocietyCharter.vue'),
  },
  {
    path: '/about/council',
    name: 'Council',
    component: () => import(/* webpackChunkName: "about" */ '../views/Council.vue'),
  },
  {
    path: '/about/organization',
    name: 'OrganizationalStructure',
    component: () => import(/* webpackChunkName: "about" */ '../views/OrganizationalStructure.vue'),
  },
  {
    path: '/about/history',
    name: 'ChronicleOfEvents',
    component: () => import(/* webpackChunkName: "about" */ '../views/ChronicleOfEvents.vue'),
  },
  // --- 其他路由保持不变 ---
  {    path: '/news',
    name: 'News',
    component: () => import(/* webpackChunkName: "News" */ '../views/News.vue'),
  },
  // --- 会员服务路由 ---
  {
    path: '/member/conferences',
    name: 'Conferences',
    component: () => import(/* webpackChunkName: "member" */ '../views/member/Conferences.vue'),
  },
  {
    path: '/member/education',
    name: 'ContinuingEducation',
    component: () => import(/* webpackChunkName: "member" */ '../views/member/ContinuingEducation.vue'),
  },
  {
    path: '/member/standards',
    name: 'StandardsQuery',
    component: () => import(/* webpackChunkName: "member" */ '../views/member/StandardsQuery.vue'),
  },
  {
    path: '/banner/:id',
    name: 'BannerDetail',
    props: true,
    component: () => import(/* webpackChunkName: "banner-detail" */ '../views/BannerDetail.vue'),
  },
  {
    path: '/activity/:id',
    name: 'ActivityDetail',
    props: true,
    component: () => import(/* webpackChunkName: "activity-detail" */ '../views/ActivityDetail.vue'),
  },
  {
    path: '/member-guidelines',
    name: 'MemberGuidelines',
    component: () => import(/* webpackChunkName: "member-guidelines" */ '../views/MemberGuidelines.vue'),
  },
  {
    path: '/news/:id',
    name: 'NewsDetail',
    props: true,
    component: () => import(/* webpackChunkName: "news-detail" */ '../views/NewsDetail.vue'),
  },
  {
    path: '/resources',
    name: 'AcademicResources',
    component: () => import(/* webpackChunkName: "academic-resources" */ '../views/AcademicResources.vue'),
    meta: { requiresAuth: true, requiresPaid: true },
  },
  {
    path: '/forum',
    name: 'Forum',
    component: () => import(/* webpackChunkName: "forum" */ '../views/Forum.vue'),
    meta: { requiresAuth: true, requiresPaid: true },
  },
  {
    path: '/forum/topic/:id',
    name: 'ForumDetail',
    props: true,
    component: () => import(/* webpackChunkName: "forum-detail" */ '../views/ForumDetail.vue'),
    meta: { requiresAuth: true, requiresPaid: true },
  },
  {
    path: '/forum/edit/:id',
    name: 'EditPost',
    props: true,
    component: () => import(/* webpackChunkName: "edit-post" */ '../views/EditPost.vue'),
    meta: { requiresAuth: true, requiresPaid: true },
  },
// --- 新增路由规则 ---
  {
    path: '/new-post',
    name: 'NewsPost',
    component: () => import('../views/NewsPost.vue'),
  },  {
    path: '/register',
    name: 'Register',
    component: () => import(/* webpackChunkName: "register" */ '../views/Register.vue'),
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import(/* webpackChunkName: "login" */ '../views/Login.vue')
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import(/* webpackChunkName: "profile" */ '../views/Profile.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/payment',
    name: 'Payment',
    component: () => import(/* webpackChunkName: "payment" */ '../views/Payment.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/payment-required',
    name: 'PaymentRequired',
    component: () => import(/* webpackChunkName: "payment-required" */ '../views/PaymentRequired.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/payment-result',
    name: 'PaymentResult',
    component: () => import(/* webpackChunkName: "payment-result" */ '../views/PaymentResult.vue'),
  },
  {
    path: '*',
    name: 'NotFound',
    component: () => import(/* webpackChunkName: "not-found" */ '../views/NotFound.vue')
  }
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

// 全局路由守卫
router.beforeEach((to, from, next) => {
  let token = TokenService.getToken()
  if (token && TokenService.isTokenExpired(token)) {
    TokenService.removeToken()
    localStorage.removeItem('userInfo')
    token = null
  }
  
  // 如果目标路由是登录页
  if (to.path === '/login') {
    // 如果已经登录，重定向到首页或指定页面
    if (token) {
      const redirect = to.query.redirect || '/'
      // 避免重定向到需要付费权限的页面造成循环重定向
      // 如果重定向目标需要付费权限，先检查用户付费状态
      const needsPaidAccess = ['/forum', '/resources'].some(path => redirect.startsWith(path))
      
      if (needsPaidAccess) {
        // 检查用户付费状态
        const userInfo = localStorage.getItem('userInfo')
        let localPaidStatus = false
        
        if (userInfo) {
          try {
            const user = JSON.parse(userInfo)
            localPaidStatus = user.feeStatus === 'paid'
          } catch (e) {
            console.error('解析用户信息失败:', e)
          }
        }
        
        if (localPaidStatus) {
          // 已付费，可以重定向到目标页面
          next(redirect)
        } else {
          // 未付费，重定向到首页避免循环
          next('/')
        }
      } else {
        // 不需要付费权限的页面，直接重定向
        next(redirect)
      }
      return
    }
    // 未登录，正常访问登录页
    next()
    return
  }
  
  // 检查路由是否需要认证
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!token) {
      // 没有token，重定向到登录页
      next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
      return
    }
    
    // 检查是否需要付费权限
    if (to.matched.some(record => record.meta.requiresPaid)) {
      // 首先检查localStorage中的缓存状态
      const userInfo = localStorage.getItem('userInfo')
      let localPaidStatus = false
      
      if (userInfo) {
        try {
          const user = JSON.parse(userInfo)
          localPaidStatus = user.feeStatus === 'paid'
        } catch (e) {
          console.error('解析用户信息失败:', e)
        }
      }
      
      // 如果本地状态显示已付费，直接允许访问
      if (localPaidStatus) {
        next()
        return
      }
      
      // 如果本地状态显示未付费，通过API检查支付历史
      getPaymentHistory({ page: 1, size: 50 })
        .then(response => {
          if (response.code === 200 && response.data && response.data.records) {
            // 检查是否有支付成功的记录
            const hasSuccessfulPayment = response.data.records.some(payment => 
              payment.status === '支付成功' || payment.status === 'PAID'
            )
            
            if (hasSuccessfulPayment) {
              // 更新本地缓存状态
              if (userInfo) {
                try {
                  const user = JSON.parse(userInfo)
                  user.feeStatus = 'paid'
                  localStorage.setItem('userInfo', JSON.stringify(user))
                  console.log('根据支付历史更新用户缴费状态为已支付')
                } catch (e) {
                  console.error('更新用户信息失败:', e)
                }
              }
              next()
            } else {
              // 确实未付费，重定向到付费提示页面
              console.log('用户未付费，重定向到付费页面')
              next({
                path: '/payment-required',
                query: { redirect: to.fullPath }
              })
            }
          } else {
            // API调用失败，重定向到付费提示页面
            console.log('无法获取支付历史，重定向到付费页面')
            next({
              path: '/payment-required',
              query: { redirect: to.fullPath }
            })
          }
        })
        .catch(error => {
          console.error('检查支付历史失败:', error)
          // API调用失败，重定向到付费提示页面
          next({
            path: '/payment-required',
            query: { redirect: to.fullPath }
          })
        })
      return
    }
    
    // 已登录且有相应权限，允许访问
    next()
  } else {
    // 不需要认证的页面，直接访问
    next()
  }
});

export default router;
